import type { ReactNode } from "react";
import { CircleQuestionMarkIcon, CircleXIcon, CloudCheckIcon } from "../shared/icons";
import { Button, Stack, Typography } from "@mui/material";
import { useTranslation, type TranslationKey } from "@privacy/translations";
import {
  assessmentFormStandardAnswersQueryOptions,
  assessmentQueryOptions,
  assessmentsFormQueryOptions,
  type AssessmentsGridViewResponse,
} from "./trustAPI";
import { queryClient } from "../api/queryClient";
import { useQuery } from "@tanstack/react-query";

const statusToInfo: Record<
  AssessmentsGridViewResponse["assessments"][number]["status"],
  { icon: ReactNode; labelKey: TranslationKey; clickable: boolean; onHover?: (id: string) => void }
> = {
  "awaiting-response": {
    icon: <CircleQuestionMarkIcon size={14} color={theme => theme.palette.primary["400"]} />,
    labelKey: "Trust.Assessment.Status.AwaitingResponse",
    clickable: true,
    onHover: (id: string) => {
      queryClient.prefetchQuery(assessmentQueryOptions(id));
      queryClient.prefetchQuery(assessmentsFormQueryOptions());
      queryClient.prefetchQuery(assessmentFormStandardAnswersQueryOptions());
    },
  },
  "responded": {
    icon: <CloudCheckIcon size={14} color={theme => theme.palette.primary["400"]} />,
    labelKey: "Trust.Assessment.Status.Responded",
    clickable: true,
    onHover: (id: string) => queryClient.prefetchQuery(assessmentQueryOptions(id)),
  },
  "withdrawn": {
    icon: <CircleXIcon size={14} color={theme => theme.palette.primary["400"]} />,
    labelKey: "Trust.Assessment.Status.Withdrawn",
    clickable: false,
  },
};

export const TrustCardContent = ({
  row,
  onCustomizeAnswersClick,
  onSeeSentAnswersClick,
  onSendStandardAnswersClick,
}: {
  row: AssessmentsGridViewResponse["assessments"][number];
  onCustomizeAnswersClick: () => void;
  onSeeSentAnswersClick: () => void;
  onSendStandardAnswersClick: () => void;
}) => {
  const standardAnswersQuery = useQuery(assessmentFormStandardAnswersQueryOptions());
  const { t, formatDate } = useTranslation();
  const { requestedBy, status, requestedAt } = row;
  const statusInfo = statusToInfo[status];
  return (
    <Stack
      onMouseEnter={() => statusInfo.onHover?.(row.id)}
      sx={[
        {
          gap: 1,
          p: 3,
          border: "1px solid",
          borderColor: theme => theme.palette.divider,
          borderRadius: 1,
          width: "100%",
        },
        /** The grid will hanle the styling of clickable rows */
        !statusInfo.clickable && {
          "cursor": "default",
          "&:hover": { backgroundColor: theme => theme.palette.background.paper },
        },
      ]}
    >
      <Stack direction="row" sx={{ alignItems: "center", width: "100%", justifyContent: "space-between" }}>
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            textTransform: "uppercase",
            fontWeight: "700",
            letterSpacing: "0.5px",
            color: theme => theme.palette.primary.main,
          }}
        >
          {t("Trust.Card.From")}
        </Typography>
        <Typography
          sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
        >
          {formatDate(requestedAt)}
        </Typography>
      </Stack>
      <Stack>
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(16), fontWeight: 600 }}>
          {requestedBy.organisation.name}
        </Typography>
        <Stack>
          <Typography
            sx={{ fontSize: theme => theme.typography.pxToRem(14), color: theme => theme.palette.text.secondary }}
          >
            {requestedBy.user.firstName} {requestedBy.user.lastName}
          </Typography>
          <Typography
            sx={{
              lineHeight: 1,
              fontSize: theme => theme.typography.pxToRem(12),
              color: theme => theme.palette.text.tertiary,
            }}
          >
            {requestedBy.user.email}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 1, mt: 2, width: "100%", justifyContent: "space-between" }}
      >
        <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
          {statusInfo.icon}
          <Typography
            sx={{
              color: theme => theme.palette.text.secondary,
              fontSize: theme => theme.typography.pxToRem(12),
              fontWeight: "600",
            }}
          >
            {t(statusInfo.labelKey)} {row.status === "responded" && formatDate(row.respondedAt)}
          </Typography>
        </Stack>

        {(() => {
          switch (status) {
            case "awaiting-response":
              if (!standardAnswersQuery.data)
                return (
                  <Button variant="contained" size="small" sx={{ visibility: "hidden" }}>
                    Placeholder
                  </Button>
                );
              return (
                <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={withoutRowSelection(onCustomizeAnswersClick)}>
                    {t("Trust.Card.CustomizeAnswers")}
                  </Button>
                  {standardAnswersQuery.data.answers.length > 0 && (
                    <Button variant="contained" size="small" onClick={withoutRowSelection(onSendStandardAnswersClick)}>
                      {t("Trust.Card.SendStandardAnswers")}
                    </Button>
                  )}
                </Stack>
              );
            case "responded":
              return (
                <Button
                  variant="text"
                  size="small"
                  sx={{ mr: -2 }}
                  onClick={withoutRowSelection(onSeeSentAnswersClick)}
                >
                  {t("Trust.Card.SeeSentAnswers")}
                </Button>
              );
          }
        })()}
      </Stack>
    </Stack>
  );
};

/** Stop propagation so clicking the action button does not also select the row. */
const withoutRowSelection = (handler: () => void) => (e: React.MouseEvent) => {
  e.stopPropagation();
  handler();
};
